import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  CircleOff,
  Clock3,
  KeyRound,
  LoaderCircle,
  Mail,
  MapPin,
  Phone,
  RefreshCcw,
  ShieldCheck,
  UserRound,
  UserRoundCheck,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlerts } from '../../components/feedback/AlertProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  activateFreeTrialRequest,
  getFreeTrialRequest,
  reviewFreeTrialRequest,
  sendFreeTrialAccountDetails,
  setFreeTrialActivation,
} from '../../api/freeTrialApi';

import '../../styles/FreeTrial/free-trial-request-details.css';

function formatDate(value) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}

export default function FreeTrialRequestDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlerts();
  const { t, isRtl } = useLanguage();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState('');
  const [reviewNote, setReviewNote] = useState('');

  const loadRequest = useCallback(async () => {
    setLoading(true);

    try {
      const result = await getFreeTrialRequest(id);
      setRequest(result);
      setReviewNote(result?.review_note || '');
    } catch (error) {
      showAlert({
        type: 'error',
        message: error.userMessage || t('freeTrial.admin.loadDetailsError'),
      });
    } finally {
      setLoading(false);
    }
  }, [id, showAlert, t]);

  useEffect(() => {
    loadRequest();
  }, [loadRequest]);

  const review = async (decision) => {
    if (!request || request.status !== 'pending' || busyAction) return;

    setBusyAction(decision);

    try {
      const updated = await reviewFreeTrialRequest(
        request.id,
        decision,
        reviewNote.trim(),
      );

      setRequest(updated);
      setReviewNote(updated.review_note || '');

      showAlert({
        type: 'success',
        message:
          decision === 'approved'
            ? t('freeTrial.admin.approvedMessage')
            : t('freeTrial.admin.declinedMessage'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        message: error.userMessage || t('freeTrial.admin.reviewError'),
      });
    } finally {
      setBusyAction('');
    }
  };

  const activateAccount = async () => {
    if (!request || busyAction) return;

    setBusyAction('activate');

    try {
      const result = await activateFreeTrialRequest(request.id);
      const updated = result.request || result;

      setRequest(updated);

      showAlert({
        type: result.credentials_sent === false ? 'warning' : 'success',
        message:
          result.credentials_sent === false
            ? t('freeTrial.admin.activationEmailWarning')
            : t('freeTrial.admin.activateSuccess'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        message: error.userMessage || t('freeTrial.admin.activationError'),
      });
    } finally {
      setBusyAction('');
    }
  };

  const toggleActivation = async () => {
    if (!request?.account?.created || busyAction) return;

    const nextActive = !request.account.active;
    setBusyAction('toggle');

    try {
      const updated = await setFreeTrialActivation(request.id, nextActive);
      setRequest(updated);

      showAlert({
        type: 'success',
        message: nextActive
          ? t('freeTrial.admin.reactivateSuccess')
          : t('freeTrial.admin.deactivateSuccess'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        message: error.userMessage || t('freeTrial.admin.activationError'),
      });
    } finally {
      setBusyAction('');
    }
  };

  const sendAccountDetails = async () => {
    if (!request?.account?.created || busyAction) return;

    setBusyAction('credentials');

    try {
      const updated = await sendFreeTrialAccountDetails(request.id);
      setRequest(updated);

      showAlert({
        type: 'success',
        message: t('freeTrial.admin.accountDetailsSent'),
      });
    } catch (error) {
      showAlert({
        type: 'error',
        message: error.userMessage || t('freeTrial.admin.accountDetailsError'),
      });
    } finally {
      setBusyAction('');
    }
  };

  if (loading) {
    return (
      <div className="free-trial-request-details-loading">
        <LoaderCircle className="free-trial-request-details-spin" size={30} />
        <span>{t('freeTrial.admin.loadingDetails')}</span>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="free-trial-request-details-empty">
        <CircleOff size={30} />
        <strong>{t('freeTrial.admin.detailsNotFound')}</strong>
        <button type="button" onClick={() => navigate('/free-trial-requests')}>
          {t('freeTrial.admin.backToRequests')}
        </button>
      </div>
    );
  }

  const account = request.account || {};

  return (
    <div
      className={`free-trial-request-details-page${isRtl ? ' is-rtl' : ''}`}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <button
        type="button"
        className="free-trial-request-details-back"
        onClick={() => navigate('/free-trial-requests')}
      >
        <ArrowLeft size={16} />
        {t('freeTrial.admin.backToRequests')}
      </button>

      <header className="free-trial-request-details-header">
        <div>
          <small>{t('freeTrial.admin.requestNumber', { id: request.id })}</small>
          <h1>{request.business_name || request.email}</h1>
          <p>{request.applicant_name || request.email}</p>
        </div>

        <div className="free-trial-request-details-header__states">
          <span className={`free-trial-request-details-status status-${request.status}`}>
            {t(`freeTrial.admin.status.${request.status}`)}
          </span>

          {account.created ? (
            <span className={`free-trial-request-details-account-state ${account.active ? 'is-active' : 'is-inactive'}`}>
              {account.active ? <CheckCircle2 size={14} /> : <CircleOff size={14} />}
              {account.active
                ? t('freeTrial.admin.accountActive')
                : t('freeTrial.admin.accountInactive')}
            </span>
          ) : null}
        </div>
      </header>

      <section className="free-trial-request-details-summary">
        <article>
          <span><Mail size={17} /></span>
          <div>
            <small>{t('freeTrial.admin.email')}</small>
            <strong>{request.email}</strong>
          </div>
        </article>

        <article>
          <span><ShieldCheck size={17} /></span>
          <div>
            <small>{t('freeTrial.admin.emailVerified')}</small>
            <strong>{formatDate(request.email_verified_at)}</strong>
          </div>
        </article>

        <article>
          <span><Clock3 size={17} /></span>
          <div>
            <small>{t('freeTrial.admin.submittedAt')}</small>
            <strong>{formatDate(request.submitted_at)}</strong>
          </div>
        </article>

        <article>
          <span><Building2 size={17} /></span>
          <div>
            <small>{t('freeTrial.admin.businessType')}</small>
            <strong>
              {request.business_type
                ? t(`freeTrial.businessTypes.${request.business_type}`)
                : '—'}
            </strong>
          </div>
        </article>
      </section>

      <div className="free-trial-request-details-layout">
        <div className="free-trial-request-details-main">
          <section className="free-trial-request-details-card">
            <div className="free-trial-request-details-card__title">
              <UserRound size={18} />
              <div>
                <h2>{t('freeTrial.admin.applicationDetails')}</h2>
                <p>{t('freeTrial.admin.applicationDetailsDescription')}</p>
              </div>
            </div>

            <div className="free-trial-request-details-grid">
              <div>
                <small>{t('freeTrial.fields.applicantName')}</small>
                <strong>{request.applicant_name || '—'}</strong>
              </div>
              <div>
                <small>{t('freeTrial.fields.phone')}</small>
                <strong>{request.phone || '—'}</strong>
              </div>
              <div>
                <small>{t('freeTrial.fields.defaultLanguage')}</small>
                <strong>
                  {request.default_language
                    ? t(`languages.${request.default_language}`)
                    : '—'}
                </strong>
              </div>
              <div>
                <small>{t('freeTrial.admin.requestLanguage')}</small>
                <strong>
                  {request.request_locale
                    ? t(`languages.${request.request_locale}`)
                    : '—'}
                </strong>
              </div>
            </div>
          </section>

          <section className="free-trial-request-details-card">
            <div className="free-trial-request-details-card__title">
              <Building2 size={18} />
              <div>
                <h2>{t('freeTrial.admin.businessDetails')}</h2>
                <p>{t('freeTrial.admin.businessDetailsDescription')}</p>
              </div>
            </div>

            <div className="free-trial-request-details-grid">
              <div>
                <small>{t('freeTrial.fields.businessName')}</small>
                <strong>{request.business_name || '—'}</strong>
              </div>
              <div>
                <small>{t('freeTrial.fields.businessType')}</small>
                <strong>
                  {request.business_type
                    ? t(`freeTrial.businessTypes.${request.business_type}`)
                    : '—'}
                </strong>
              </div>
              <div>
                <small>{t('freeTrial.fields.businessEmail')}</small>
                <strong>{request.business_email || '—'}</strong>
              </div>
              <div>
                <small>{t('freeTrial.fields.businessPhone')}</small>
                <strong>{request.business_phone || '—'}</strong>
              </div>
              <div className="is-wide">
                <small>{t('freeTrial.fields.whatsapp')}</small>
                <strong>{request.business_whatsapp || '—'}</strong>
              </div>
              <div className="is-wide">
                <small>{t('freeTrial.fields.address')}</small>
                <strong>{request.business_address || '—'}</strong>
              </div>
              <div className="is-wide">
                <small>{t('freeTrial.fields.description')}</small>
                <strong>{request.business_description || '—'}</strong>
              </div>
            </div>
          </section>

          <section className="free-trial-request-details-card">
            <div className="free-trial-request-details-card__title">
              <Phone size={18} />
              <div>
                <h2>{t('freeTrial.admin.contactDetails')}</h2>
                <p>{t('freeTrial.admin.contactDetailsDescription')}</p>
              </div>
            </div>

            <div className="free-trial-request-contact-actions">
              <a href={`mailto:${request.email}`}>
                <Mail size={16} />
                <span>{request.email}</span>
              </a>

              {request.business_phone || request.phone ? (
                <a href={`tel:${request.business_phone || request.phone}`}>
                  <Phone size={16} />
                  <span>{request.business_phone || request.phone}</span>
                </a>
              ) : null}

              {request.business_address ? (
                <div>
                  <MapPin size={16} />
                  <span>{request.business_address}</span>
                </div>
              ) : null}
            </div>
          </section>
        </div>

        <aside className="free-trial-request-details-side">
          <section className="free-trial-request-details-card free-trial-request-details-review">
            <div className="free-trial-request-details-card__title">
              <ShieldCheck size={18} />
              <div>
                <h2>{t('freeTrial.admin.reviewDetails')}</h2>
                <p>{t('freeTrial.admin.reviewDetailsDescription')}</p>
              </div>
            </div>

            {request.status === 'pending' ? (
              <>
                <label>
                  <span>{t('freeTrial.admin.reviewNote')}</span>
                  <textarea
                    rows={5}
                    value={reviewNote}
                    onChange={(event) => setReviewNote(event.target.value)}
                    placeholder={t('freeTrial.admin.reviewNotePlaceholder')}
                  />
                </label>

                <div className="free-trial-request-review-actions">
                  <button
                    type="button"
                    className="is-decline"
                    disabled={Boolean(busyAction)}
                    onClick={() => review('declined')}
                  >
                    {busyAction === 'declined' ? (
                      <LoaderCircle className="free-trial-request-details-spin" size={16} />
                    ) : (
                      <XCircle size={16} />
                    )}
                    {t('freeTrial.admin.decline')}
                  </button>

                  <button
                    type="button"
                    className="is-approve"
                    disabled={Boolean(busyAction)}
                    onClick={() => review('approved')}
                  >
                    {busyAction === 'approved' ? (
                      <LoaderCircle className="free-trial-request-details-spin" size={16} />
                    ) : (
                      <CheckCircle2 size={16} />
                    )}
                    {t('freeTrial.admin.approve')}
                  </button>
                </div>
              </>
            ) : (
              <div className="free-trial-request-reviewed-box">
                <strong>
                  {request.status === 'approved'
                    ? t('freeTrial.admin.approved')
                    : t('freeTrial.admin.declined')}
                </strong>
                <span>{formatDate(request.reviewed_at)}</span>
                {request.reviewer?.name ? <span>{request.reviewer.name}</span> : null}
                {request.review_note ? <p>{request.review_note}</p> : null}
              </div>
            )}
          </section>

          <section className="free-trial-request-details-card free-trial-request-details-account">
            <div className="free-trial-request-details-card__title">
              <KeyRound size={18} />
              <div>
                <h2>{t('freeTrial.admin.activationDetails')}</h2>
                <p>{t('freeTrial.admin.activationDetailsDescription')}</p>
              </div>
            </div>

            {!account.created ? (
              <div className="free-trial-request-account-empty">
                <CircleOff size={23} />
                <strong>{t('freeTrial.admin.accountNotCreated')}</strong>
                <p>{t('freeTrial.admin.accountNotCreatedDescription')}</p>

                {request.status === 'approved' ? (
                  <button
                    type="button"
                    onClick={activateAccount}
                    disabled={Boolean(busyAction)}
                  >
                    {busyAction === 'activate' ? (
                      <LoaderCircle className="free-trial-request-details-spin" size={16} />
                    ) : (
                      <UserRoundCheck size={16} />
                    )}
                    {t('freeTrial.admin.activate')}
                  </button>
                ) : null}
              </div>
            ) : (
              <>
                <div className="free-trial-request-account-info">
                  <div>
                    <small>{t('freeTrial.admin.accountEmail')}</small>
                    <strong>{account.manager?.email || request.email}</strong>
                  </div>
                  <div>
                    <small>{t('freeTrial.admin.accountCreatedAt')}</small>
                    <strong>{formatDate(account.created_at)}</strong>
                  </div>
                  <div>
                    <small>{t('freeTrial.admin.credentialsSentAt')}</small>
                    <strong>{formatDate(account.credentials_sent_at)}</strong>
                  </div>
                  <div>
                    <small>{t('freeTrial.admin.lastLogin')}</small>
                    <strong>{formatDate(account.manager?.last_login_at)}</strong>
                  </div>
                  <div>
                    <small>{t('freeTrial.admin.userId')}</small>
                    <strong>#{account.user_id}</strong>
                  </div>
                  <div>
                    <small>{t('freeTrial.admin.businessId')}</small>
                    <strong>#{account.business_id}</strong>
                  </div>
                  <div>
                    <small>{t('freeTrial.admin.establishmentId')}</small>
                    <strong>#{account.establishment_id}</strong>
                  </div>
                </div>

                <div className="free-trial-request-account-actions">
                  <button
                    type="button"
                    className="is-credentials"
                    disabled={Boolean(busyAction)}
                    onClick={sendAccountDetails}
                  >
                    {busyAction === 'credentials' ? (
                      <LoaderCircle className="free-trial-request-details-spin" size={16} />
                    ) : (
                      <Mail size={16} />
                    )}
                    {t('freeTrial.admin.sendAccountDetails')}
                  </button>

                  <button
                    type="button"
                    className={account.active ? 'is-deactivate' : 'is-reactivate'}
                    disabled={Boolean(busyAction)}
                    onClick={toggleActivation}
                  >
                    {busyAction === 'toggle' ? (
                      <LoaderCircle className="free-trial-request-details-spin" size={16} />
                    ) : account.active ? (
                      <CircleOff size={16} />
                    ) : (
                      <RefreshCcw size={16} />
                    )}
                    {account.active
                      ? t('freeTrial.admin.deactivate')
                      : t('freeTrial.admin.reactivate')}
                  </button>
                </div>
              </>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}